/**
 * The client's save, built on the total snapshot.
 *
 * A save is three things and the split is the whole design (ADR-006):
 *
 *   - **the snapshot** — the district, entire, exactly as a settlement worker
 *     would be handed it. Not a second serialisation format that happens to
 *     resemble one.
 *   - **the view** — what a district does not have. A toolbar, a brush size, an
 *     unlock the player earned, a slot name. None of it is state a season is
 *     settled from, and none of it belongs in a snapshot.
 *   - **the settings** — the parameters this session was played *under*. A
 *     snapshot restored under different parameters is a different world
 *     (ADR-005 D16), so a save that carried the world and not its parameters
 *     would load into a world the player never played.
 *
 * Everything above the `SaveLoadSystem` class is pure: what a save contains, and
 * how a world becomes one. The class below it is slots, storage and the player —
 * and its three ambient dependencies (a key-value store, a name prompt, the wall
 * clock) are injected rather than reached for, so all of it runs in Node too.
 * Which is why `tests/save.test.ts` can prove a save is total, and can fill a
 * quota to see what happens: the thing the old lossy format was never able to
 * claim, and the path it was never able to test.
 *
 * ### No migration is owed
 *
 * ADR-005 D22: the game was never really released, so there is no save worth
 * carrying forward. Records this build cannot read are dropped from the index
 * and their storage reclaimed, which matters more than it sounds — a district
 * snapshot is about a megabyte, and a stale v1 array sitting in the same origin
 * is a megabyte the next autosave cannot have.
 */

import type { ExportedSettings } from '../../../engine/ConfigRegistry';
import {
  DistrictSnapshot,
  EventBus,
  restoreDistrict,
  SnapshotableColony,
  SnapshotableSurface,
  snapshotDistrict,
} from '@antification/core';

/**
 * The save format's version.
 *
 * 2 was the first one built on `DistrictSnapshot`. Version 1 was the lossy
 * record this class used to write; nothing reads it, by decision rather than by
 * omission.
 *
 * 3 drops `screenX`/`screenY` from every surfaced ant (ADR-009). A version 2
 * record would in fact load — the two extra keys are inert — and that is
 * exactly why the number moves: a save whose ants carry a projection through a
 * camera that no longer exists is a different document, and it should be
 * refused loudly rather than accepted quietly. No migration is owed (ADR-005
 * D22).
 *
 * 4 follows `SNAPSHOT_VERSION` to 2, which added the district's Chronicle
 * (ADR-012). This one a version 3 record genuinely cannot survive: the surface
 * now requires a `season` key on load, and a save written before seasons existed
 * has none.
 *
 * 5 follows `SNAPSHOT_VERSION` to 3, which added the grid connection (ADR-013).
 * Same story: the surface requires a `power` key, and a save written before the
 * district had a meter has none. Third time; the justification is unchanged
 * (ADR-005 D22), and it is worth saying plainly that it will keep happening
 * through Phase 2 as each flow lands.
 *
 * 6 follows `SNAPSHOT_VERSION` to 4 and the mains (ADR-014). As predicted.
 *
 * 7 follows `SNAPSHOT_VERSION` to 5 and the federal estate (ADR-015). As
 * predicted twice: the surface now requires a `facilities` key, and a save
 * written before the district could be built on has none. This is the first bump
 * whose absence would be *visible* rather than merely wrong — a version 6 save
 * whose terrain carries `SEALED` cells would load into a district with a slab, no
 * building on it, and nothing that could say why.
 *
 * 8 follows `SNAPSHOT_VERSION` to 6 and the labour market (ADR-016). As predicted
 * three times. A version 7 save has no `labour` key, and its cars have no second
 * end to their trips — a commuter restored out of one would be driving to a house
 * id that means a data centre.
 *
 * 9 follows `SNAPSHOT_VERSION` to 7 and the rent (ADR-017). A version 8 save's
 * treasury cannot say how much of its money was rent, and its labour market
 * cannot say who was at work.
 *
 * 10 follows `SNAPSHOT_VERSION` to 8 and the households (ADR-018). A version 9
 * save has no `welfare` key, so it cannot say how long anybody has been in the
 * dark — and the families it would restore are families whose departure the
 * Chronicle has already counted.
 *
 * 11 follows `SNAPSHOT_VERSION` to 9 and the patience reservoir (ADR-019).
 *
 * 12 follows `SNAPSHOT_VERSION` to 10 and the mercy builds (ADR-020). A version 11
 * save has no `mercy` key and no `planted` list in its terrain, so a district that
 * had deeded a meadow and planted a hedgerow would load back with the promise gone
 * and the bushes as grass. The tenth bump in the phase, as predicted at the fifth.
 *
 * 13 follows `SNAPSHOT_VERSION` to 11 and the fiscal account (ADR-021). A version 12
 * save has no `fiscal` key, so a district loaded from one would have no record of
 * what it has already been billed for — and would be billed for all of it again on
 * its next cycle.
 *
 * 14 follows `SNAPSHOT_VERSION` to 12 and the corridor (ADR-022). A version 13 save
 * has no `freight` key, so a district loaded from one has its interchanges
 * demolished and its tolls forgotten — and every truck in its traffic loses the
 * cargo that was the only thing telling it where to go.
 *
 * 15 follows `SNAPSHOT_VERSION` to 13 and the world market (ADR-025). A version 14
 * save has no `market` key, so a district loaded from one forgets every price it
 * was ever quoted: it sells its compute at whatever this build ships and borrows
 * at whatever the settings panel is on, rather than at what the world had posted.
 *
 * 16 follows `SNAPSHOT_VERSION` to 14 and construction (ADR-029). A version 15 save
 * has facilities with no opening date and no build record, so a district loaded
 * from one has a slab that is neither open nor going up, a hoard back at zero — two
 * seasons of somebody's lorries un-delivered — and a wear mark at zero, which takes
 * every point of road those lorries ever cost off the road again in a single cycle.
 */
export const SAVE_VERSION = 16;

/** Where the list of slots lives. Small: metadata only, no snapshots. */
const SAVES_INDEX_KEY = 'antification-saves';

/** One record per slot, so an autosave rewrites one save and not all of them. */
const SAVE_KEY_PREFIX = 'antification-save:';

/** The v1 single-save key. Removed on sight; see the header on migration. */
const LEGACY_SAVE_KEY = 'antification-save';

/**
 * The client's own state, which the district has no opinion about.
 *
 * Deliberately not the camera. Pan and zoom live on `ParkingtonLayer.camera`
 * and a load leaves them alone, so loading a save does not move the player's
 * view — which is the existing behaviour and the better one. If a reason to
 * carry them appears, this is the interface it goes in, and it is now the only
 * plausible one: since ADR-009 the camera is not reachable from the snapshot.
 */
export interface ViewSave {
  precisionUnlocked: boolean;
  cellsChanged: number;
  currentBrushSize: number;
}

/** A save, as it sits in storage. A JSON document, like the snapshot inside it. */
export interface GameSave {
  version: number;

  /**
   * Wall-clock, in the player's timezone, for "saved 3 min ago".
   *
   * The one honest use of `Date.now()` in the save path: it describes when a
   * person pressed a button, not when anything happened in the district. The
   * district's own clock is `snapshot.tick`.
   */
  timestamp: number;

  isAutosave: boolean;
  name?: string;

  snapshot: DistrictSnapshot;
  view: ViewSave;

  /**
   * The parameters this session was played under.
   *
   * Not part of the snapshot, deliberately (ADR-005 D16): a district is settled
   * *under* parameters rather than carrying them. But a save that restored the
   * world and not its parameters would load into a world the player never
   * played, so the client's record keeps both and keeps them apart.
   */
  settings: ExportedSettings;
}

/** What the picker needs to draw a row. No snapshot is parsed to build one. */
export interface SaveSlot {
  id: string;
  name?: string;
  isAutosave: boolean;
  timestamp: number;
  version: number;
  /** The district's tick, which is what actually distinguishes two saves. */
  tick: number;
}

/**
 * A district as the save path sees it: two halves and the seed that made them.
 *
 * Structural, so this module imports neither layer — the same reason
 * `core/snapshot.ts` takes interfaces (ADR-005 D21). When Phase 2 gives the game
 * a real `District`, it will satisfy this without being told.
 */
export interface SaveableDistrict {
  readonly seed: string;
  readonly surface: SnapshotableSurface;
  readonly colonies: readonly SnapshotableColony[];
}

/**
 * Everything a save needs to read and write, from either direction.
 *
 * One interface for both, so that a test can stub it once and so that a field
 * cannot be saved without also being loaded — the drift that made the old format
 * lossy in the first place.
 */
export interface SaveContext {
  readonly district: SaveableDistrict;
  getView(): ViewSave;
  applyView(view: ViewSave): void;
  getSettings(): ExportedSettings;
  applySettings(settings: ExportedSettings): void;
}

export interface SaveMeta {
  timestamp: number;
  isAutosave: boolean;
  name?: string;
}

/** Take a save. Pure: no storage, no prompt, no clock. */
export function buildGameSave(ctx: SaveContext, meta: SaveMeta): GameSave {
  return {
    version: SAVE_VERSION,
    timestamp: meta.timestamp,
    isAutosave: meta.isAutosave,
    name: meta.name,
    snapshot: snapshotDistrict(
      ctx.district.seed,
      ctx.district.surface,
      ctx.district.colonies
    ),
    view: { ...ctx.getView() },
    // Taken as given: `ConfigRegistry.exportSettings()` builds a fresh document
    // on every call, so there is nothing here to alias.
    settings: ctx.getSettings(),
  };
}

/**
 * Become the world a save describes.
 *
 * Throws rather than doing its best, for the reason `core/snapshot.ts` gives: a
 * partly-understood save produces a world that is subtly not the one that was
 * written, and produces it silently. The version is checked before anything is
 * touched, and `restoreDistrict` checks the colony count before it touches
 * either half.
 *
 * District first, then the view, then the settings — parameters last, because
 * the config listeners they drive push values into managers that have just been
 * replaced.
 */
export function applyGameSave(record: GameSave, ctx: SaveContext): void {
  if (record.version !== SAVE_VERSION) {
    throw new RangeError(
      `save is version ${record.version}; this build reads version ${SAVE_VERSION}`
    );
  }

  restoreDistrict(record.snapshot, ctx.district.surface, ctx.district.colonies);
  ctx.applyView(record.view);
  ctx.applySettings(record.settings);
}

/**
 * The slice of `localStorage` a save needs.
 *
 * Named and injected rather than reached for, which is the same move Phase 1 made
 * on `Math.random` and the wall clock: an ambient dependency is one nothing can
 * test and nothing can substitute. `localStorage` satisfies this structurally, so
 * the browser passes nothing and a test passes a `Map`.
 */
export interface KeyValueStore {
  readonly length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Everything about saving that is not the save.
 *
 * Storage, the player, and the wall clock. All three have browser defaults and
 * all three are replaceable, which is what lets `tests/save.test.ts` exercise slot
 * rotation and a full quota in Node.
 */
export interface SaveEnvironment {
  storage: KeyValueStore;
  /** Ask for a name. `null` means the player cancelled and no save happens. */
  askName(defaultName: string): string | null;
  /** Wall-clock ms: when a person pressed a button. Never the district's clock. */
  now(): number;
}

/**
 * Save slots in a key-value store, and the UI around them.
 *
 * One key per record plus one small index. The old format kept every save in a
 * single array *and* a duplicate of the newest one, which meant an autosave
 * parsed and rewrote every save in storage — affordable for a lossy record and
 * not for a megabyte-a-slot snapshot.
 */
export class SaveLoadSystem {
  private readonly MAX_AUTOSAVES = 3;

  private readonly storage: KeyValueStore;
  private readonly askName: (defaultName: string) => string | null;
  private readonly now: () => number;

  constructor(private events: EventBus, env: Partial<SaveEnvironment> = {}) {
    // Resolved here rather than at module load, so importing this file in Node
    // does not require a browser to exist.
    this.storage = env.storage ?? localStorage;
    this.askName = env.askName ?? ((defaultName) => window.prompt('Name this save:', defaultName));
    this.now = env.now ?? (() => Date.now());
  }

  /** Write the current district to a slot. Prompts for a name unless autosaving. */
  saveGame(isAutosave: boolean, ctx: SaveContext): void {
    const existing = this.readIndex();

    let name: string | undefined;
    if (!isAutosave) {
      const defaultName = this.getDefaultSaveName(existing);
      const entered = this.askName(defaultName);
      if (entered === null) return;
      name = entered.trim() || defaultName;
    }

    const timestamp = this.now();
    const record = buildGameSave(ctx, { timestamp, isAutosave, name });
    const slot: SaveSlot = {
      id: this.newSlotId(timestamp, existing),
      name,
      isAutosave: record.isAutosave,
      timestamp,
      version: record.version,
      tick: record.snapshot.tick,
    };

    try {
      // Autosaves are a rolling window; manual saves are the player's and are
      // only ever dropped by the quota handler below.
      const kept = [slot, ...existing];
      const autosaves = kept.filter(s => s.isAutosave);
      for (const stale of autosaves.slice(this.MAX_AUTOSAVES)) {
        this.forget(kept, stale);
      }

      const size = this.commit(slot, record, kept);
      this.housekeep(kept);

      if (!isAutosave) {
        this.events.emit('NOTIFICATION_SHOW', {
          message: `Game saved! (${slot.name ?? 'unnamed'}) (${(size / 1024).toFixed(0)} KB)`,
          type: 'success',
        });
      }
    } catch (e) {
      console.error('Failed to save game:', e);
      if (!isAutosave) {
        this.events.emit('NOTIFICATION_SHOW', {
          message: 'Failed to save game!',
          type: 'warning',
        });
      }
    }
  }

  /**
   * Store one record and publish the index that names it.
   *
   * Record first: an index entry pointing at a save that was never written is a
   * slot that fails to load, while an unnamed record is merely wasted space the
   * next `housekeep` reclaims.
   *
   * On a full quota the oldest *other* save is given up and the write retried,
   * however many times that takes. The old policy sacrificed manual saves before
   * autosaves, which is exactly backwards — one was asked for and the other
   * happened on a timer.
   */
  /** Returns the record's size in characters, for the player's notification. */
  private commit(slot: SaveSlot, record: GameSave, index: SaveSlot[]): number {
    const json = JSON.stringify(record);

    for (;;) {
      try {
        this.storage.setItem(this.recordKey(slot.id), json);
        break;
      } catch (e) {
        if (!isQuotaError(e)) throw e;
        const victim = oldest(index.filter(s => s.id !== slot.id));
        // Nothing left to give up but the save being written.
        if (!victim) throw e;
        this.forget(index, victim);
      }
    }

    this.storage.setItem(SAVES_INDEX_KEY, JSON.stringify(index));
    return json.length;
  }

  /** Drop a slot from the index and delete the record it named. */
  private forget(index: SaveSlot[], slot: SaveSlot): void {
    const at = index.indexOf(slot);
    if (at >= 0) index.splice(at, 1);
    try {
      this.storage.removeItem(this.recordKey(slot.id));
    } catch {
      // A storage that refuses removal is one there is nothing useful to do about.
    }
  }

  /**
   * Reclaim what nothing points at.
   *
   * The v1 single-save key, and any record whose slot is gone — which is what a
   * tab closed between the two writes in `commit` leaves behind. Both are dead
   * megabytes in a five-megabyte budget.
   */
  private housekeep(index: SaveSlot[]): void {
    try {
      this.storage.removeItem(LEGACY_SAVE_KEY);

      const live = new Set(index.map(s => this.recordKey(s.id)));
      const orphans: string[] = [];
      for (let i = 0; i < this.storage.length; i++) {
        const key = this.storage.key(i);
        if (key && key.startsWith(SAVE_KEY_PREFIX) && !live.has(key)) {
          orphans.push(key);
        }
      }
      for (const key of orphans) this.storage.removeItem(key);
    } catch {
      // Housekeeping is never worth failing a save over.
    }
  }

  /** Every readable slot, newest first. Cheap enough to call every frame. */
  getSaveSlots(): SaveSlot[] {
    return this.readIndex();
  }

  private readIndex(): SaveSlot[] {
    let raw: string | null = null;
    try {
      raw = this.storage.getItem(SAVES_INDEX_KEY);
    } catch {
      return [];
    }
    if (!raw) return [];

    let parsed: unknown = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      // Unparseable is the same as unreadable, and handled below.
    }

    const entries = Array.isArray(parsed) ? parsed.filter(isReadableSlot) : [];
    if (!Array.isArray(parsed) || entries.length !== parsed.length) {
      // Whatever else was under this key, this build cannot read it and owes it
      // nothing (ADR-005 D22). Rewriting rather than ignoring is the point: a v1
      // save array is a megabyte the next snapshot needs.
      try {
        this.storage.setItem(SAVES_INDEX_KEY, JSON.stringify(entries));
      } catch {
        // Then the quota handler in `commit` will deal with it.
      }
      this.housekeep(entries);
    }

    return entries.sort((a, b) => b.timestamp - a.timestamp);
  }

  private readRecord(id: string): GameSave | null {
    try {
      const json = this.storage.getItem(this.recordKey(id));
      if (!json) return null;
      return JSON.parse(json) as GameSave;
    } catch {
      return null;
    }
  }

  private recordKey(id: string): string {
    return `${SAVE_KEY_PREFIX}${id}`;
  }

  /** Unique within the index, and stable in storage once written. */
  private newSlotId(timestamp: number, index: readonly SaveSlot[]): string {
    const taken = new Set(index.map(s => s.id));
    let id = String(timestamp);
    let suffix = 1;
    while (taken.has(id)) id = `${timestamp}-${suffix++}`;
    return id;
  }

  private getDefaultSaveName(slots: readonly SaveSlot[]): string {
    let maxIndex = 0;
    for (const slot of slots) {
      if (!slot.name) continue;
      const match = slot.name.match(/^antification-(\d+)$/);
      if (match) {
        maxIndex = Math.max(maxIndex, Number(match[1]));
      }
    }
    return `antification-${maxIndex + 1}`;
  }

  /** Load the slot at `slotIndex` of `getSaveSlots()`. */
  loadGame(slotIndex: number, ctx: SaveContext): void {
    const slots = this.readIndex();
    if (slots.length === 0) {
      this.events.emit('NOTIFICATION_SHOW', {
        message: 'No saved game found!',
        type: 'info',
      });
      return;
    }

    const slot = slots[slotIndex];
    if (!slot) {
      this.events.emit('NOTIFICATION_SHOW', {
        message: 'Invalid save slot!',
        type: 'warning',
      });
      return;
    }

    const record = this.readRecord(slot.id);
    if (!record) {
      this.events.emit('NOTIFICATION_SHOW', {
        message: 'That save is missing!',
        type: 'warning',
      });
      return;
    }

    try {
      applyGameSave(record, ctx);
    } catch (e) {
      console.error('Failed to load game:', e);
      this.events.emit('NOTIFICATION_SHOW', {
        message: 'Failed to load game!',
        type: 'warning',
      });
      return;
    }

    const timeAgo = this.formatTimeAgo(record.timestamp);
    const autoLabel = record.isAutosave ? ' (autosave)' : '';
    this.events.emit('NOTIFICATION_SHOW', {
      message: `Game loaded!${autoLabel} (saved ${timeAgo})`,
      type: 'success',
    });

    console.log(
      'Game loaded from slot', slotIndex,
      '- saved:', new Date(record.timestamp).toLocaleString(),
      'at tick', record.snapshot.tick
    );
  }

  /** Format a timestamp as relative time. */
  formatTimeAgo(timestamp: number): string {
    const seconds = Math.floor((this.now() - timestamp) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hr ago`;
    return `${Math.floor(seconds / 86400)} days ago`;
  }

  hasSavedGame(): boolean {
    return this.readIndex().length > 0;
  }
}

/** A slot this build can act on. Anything else is a v1 leftover. */
function isReadableSlot(value: unknown): value is SaveSlot {
  if (typeof value !== 'object' || value === null) return false;
  const slot = value as Partial<SaveSlot>;
  return (
    typeof slot.id === 'string' &&
    typeof slot.timestamp === 'number' &&
    slot.version === SAVE_VERSION
  );
}

function oldest(slots: readonly SaveSlot[]): SaveSlot | null {
  let found: SaveSlot | null = null;
  for (const slot of slots) {
    if (!found || slot.timestamp < found.timestamp) found = slot;
  }
  return found;
}

/**
 * A full storage, by any of the three names browsers give it.
 *
 * By name rather than by class, which is a fix and not a style preference: the old
 * code tested `e instanceof DOMException`, so Firefox's quota error — a plain
 * exception with its own name — fell through to the generic handler and the save
 * simply failed instead of making room. Nothing in a five-megabyte budget can
 * afford to only half-recognise a full disk.
 */
function isQuotaError(e: unknown): boolean {
  const name = (e as { name?: unknown } | null | undefined)?.name;
  return (
    name === 'QuotaExceededError' ||        // Chrome, Safari, the spec
    name === 'NS_ERROR_DOM_QUOTA_REACHED' || // Firefox
    name === 'QUOTA_EXCEEDED_ERR'            // older WebKit
  );
}
